// LanguageProofingDrawer.tsx · v12.10.25 (#2026 Language proofing drawer, prototype UI-PROTO-LANGUAGE-PROOFING-B6 v1.4 LOCKED; #2025 AI Suggest / AI Check) · 2026-10-30 · VoiceSpectrum AI
//
// The standard 680 px drawer (works on a phone): every line the agent says in one language (Q2 - card prompts AND phrase rows,
// each saved where it lives), English beside it with where it is spoken. Click a line to edit; AI Suggest / AI Check answer in
// place with the why - click the answer to put it in the box (#2025: nothing changes until a person does that and presses Done).
// No Mark proofread: the saved copy is the proofread copy (lines opened and saved count as proofread). Done needs Settings ›
// AI Agent › Agent details edit (Q3). English opens too - a style pass, the current English stays as the reference (Q4).
// Search and the stage filter close the open line (v1.4); Esc closes the line, then the drawer; Ctrl+Enter = Done.
import { useEffect, useMemo, useRef, useState } from 'react';
import api, { ApiError } from '../../lib/api';
import './language-proofing.css';

export interface ProofLineDto { id: string; kind: string; stage: string; where: string; english: string; text: string; machine: boolean; status: 'new' | 'changed' | 'ok' }
export interface ProofingView { lang: string; primary: string; languages: string[]; workflowId: number | null; workflowUpdatedAt: string | null; lines: ProofLineDto[] }
export interface ProofingSummary { primary: string; languages: { code: string; lines: number; new: number; changed: number }[] }
interface AiResult { ok: boolean; verdict?: string | null; suggestion?: string | null; why?: string | null; reason?: string | null }

export function langFullName(code: string): string {
  const c = (code || '').toLowerCase();
  if (c === 'zh') return 'Chinese Mandarin';
  try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(c) || code; } catch { return code; }
}
export const langTitle = (code: string) => `${langFullName(code)} · ${(code || '').toUpperCase()}`;

const AI_REASONS: Record<string, string> = {
  'no-key': 'AI is not set up for this practice yet.', 'ai-timeout': 'The AI took too long - try again.', 'ai-busy': 'The AI is busy - try again in a minute.',
  'ai-rate-limit': 'Too many AI requests - wait a minute.', 'ai-auth': 'The AI key was refused - Support can check it.', 'not-found': 'This line is no longer there - reload.',
};

export default function LanguageProofingDrawer({ open, initialLang, voices, canEdit, onClose, onSaved }: {
  open: boolean; initialLang: string; voices: Record<string, string>; canEdit: boolean; onClose: () => void; onSaved?: () => void;
}) {
  const [lang, setLang] = useState(initialLang);
  const [view, setView] = useState<ProofingView | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'new' | 'chg' | 'all'>('new');
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());
  const [ai, setAi] = useState<Record<string, AiResult & { busy?: boolean; mode?: string }>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const toastT = useRef<number | undefined>(undefined);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const focusNext = useRef(false);

  function say(t: string) { if (toastT.current) window.clearTimeout(toastT.current); setToast(t); toastT.current = window.setTimeout(() => setToast(''), 3200); }

  useEffect(() => { if (open) setLang(initialLang); }, [open, initialLang]);
  useEffect(() => {
    if (!open || !lang) return;
    let live = true;
    setLoading(true); setOpenId(null); setEdits({}); setReviewed(new Set()); setAi({});
    api.get<ProofingView>(`/t2/language-proofing/${encodeURIComponent(lang)}`)
      .then((v) => { if (!live) return; setView(v); if (!v.lines.some((l) => l.status === 'new')) setFilter(v.lines.some((l) => l.status === 'changed') ? 'chg' : 'all'); else setFilter('new'); })
      .catch(() => { if (live) { setView(null); say('Could not load the lines - try again.'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, lang]);

  const isEn = !!view && view.lang === view.primary;
  const dirtyCount = Object.keys(edits).length;
  const stages = useMemo(() => Array.from(new Set((view?.lines ?? []).map((l) => l.stage))), [view]);
  const counts = useMemo(() => ({
    all: view?.lines.length ?? 0,
    nw: view?.lines.filter((l) => l.status === 'new').length ?? 0,
    chg: view?.lines.filter((l) => l.status === 'changed').length ?? 0,
  }), [view]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (view?.lines ?? []).filter((l) =>
      (filter === 'all' || (filter === 'new' && l.status === 'new') || (filter === 'chg' && l.status === 'changed') || edits[l.id] !== undefined || l.id === openId)
      && (!stage || l.stage === stage)
      && (!t || (l.english + ' ' + (edits[l.id] ?? l.text) + ' ' + l.where + ' ' + l.stage).toLowerCase().includes(t)));
  }, [view, filter, stage, q, edits, openId]);

  useEffect(() => {
    const el = taRef.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = Math.max(84, el.scrollHeight + 4) + 'px';
    if (focusNext.current) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); focusNext.current = false; }
  });

  function closeLine() { setOpenId(null); }
  function openLine(id: string) { setOpenId(id); focusNext.current = true; setReviewed((s) => new Set(s).add(id)); }
  function textOf(l: ProofLineDto) { return edits[l.id] ?? (isEn ? l.english : l.text); }
  function setText(l: ProofLineDto, v: string) {
    const base = isEn ? l.english : l.text;
    setEdits((e) => { const n = { ...e }; if (v === base) delete n[l.id]; else n[l.id] = v; return n; });
  }

  async function play(text: string, code: string) {
    const voiceId = voices[code] || voices[view?.primary ?? 'en'] || '';
    if (!text.trim()) { say('Nothing to play.'); return; }
    if (!voiceId) { say('Choose a voice for this language first (Languages & voices).'); return; }
    say('Synthesizing…');
    try {
      const r = await api.post<{ audioUrl: string | null; reason?: string | null }>('/t2/settings/ai-agent/voice-preview', { language: code, voiceId, sample: text });
      if (r.audioUrl) { say('▶ Playing'); try { await new Audio(r.audioUrl).play(); } catch { say('Tap anywhere, then play again - the browser blocked autoplay.'); } }
      else say('Could not make the audio - try again.');
    } catch { say('Could not make the audio - try again.'); }
  }

  async function askAi(l: ProofLineDto, mode: 'suggest' | 'check') {
    if (!canEdit) { say('Your group cannot use AI Suggest here.'); return; }
    setAi((a) => ({ ...a, [l.id]: { ok: false, busy: true, mode } }));
    try {
      const r = await api.post<AiResult>(`/t2/language-proofing/${encodeURIComponent(lang)}/ai`, { id: l.id, text: textOf(l), mode });
      setAi((a) => ({ ...a, [l.id]: { ...r, mode } }));
    } catch { setAi((a) => ({ ...a, [l.id]: { ok: false, reason: 'error', mode } })); }
  }

  async function done() {
    if (!view || saving) return;
    if (!canEdit) { say('Your group cannot save language lines - ask someone with Settings › AI Agent.'); return; }
    if (!dirtyCount && reviewed.size === 0) { say('Nothing to save'); return; }
    setSaving(true);
    try {
      const next = await api.put<ProofingView>(`/t2/language-proofing/${encodeURIComponent(lang)}`, {
        workflowUpdatedAt: view.workflowUpdatedAt, lines: Object.entries(edits).map(([id, text]) => ({ id, text })), reviewed: Array.from(reviewed),
      });
      say(dirtyCount ? `Saved ${dirtyCount} line${dirtyCount > 1 ? 's' : ''} - the saved copy is the proofread copy` : 'Marked the lines you read as proofread');
      setView(next); setEdits({}); setReviewed(new Set()); setOpenId(null); setAi({});
      onSaved?.();
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { message?: string } | undefined) : undefined;
      say(b?.message || 'Not saved - try again.');
    } finally { setSaving(false); }
  }

  function tryClose() {
    if (dirtyCount && !window.confirm(`${dirtyCount} changed line${dirtyCount > 1 ? 's are' : ' is'} not saved. Close anyway?`)) return;
    onClose();
  }

  useEffect(() => {
    if (!open) return;
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); if (openId) closeLine(); else tryClose(); }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void done(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  });

  function download(name: string, body: string, type: string) {
    const url = URL.createObjectURL(new Blob([body], { type }));
    const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }
  function exportAs(kind: 'json' | 'html') {
    if (!view) return;
    const rows = view.lines.map((l) => ({ id: l.id, stage: l.stage, where: l.where, english: l.english, text: textOf(l), status: l.status }));
    const stamp = new Date().toISOString().slice(0, 10);
    if (kind === 'json') { download(`language-proofing-${lang}-${stamp}.json`, JSON.stringify({ lang, primary: view.primary, lines: rows }, null, 2), 'application/json'); return; }
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Language proofing - ${esc(langTitle(lang))}</title><style>body{font:14px system-ui;margin:20px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ddd;padding:6px;vertical-align:top;text-align:left}td.tx{background:#fffbe6}</style></head><body><h2>Language proofing · ${esc(langTitle(lang))}</h2><p>Correct the right-hand column, save this file and send it back - Import puts your corrections in, marked EDITED until Done.</p><table><tr><th>Where</th><th>${esc(langTitle(view.primary))}</th><th>${esc(langTitle(lang))}</th></tr>${rows.map((r) => `<tr data-id="${esc(r.id)}"><td>${esc(r.stage)} › ${esc(r.where)}</td><td>${esc(r.english)}</td><td class="tx" contenteditable="true">${esc(r.text)}</td></tr>`).join('')}</table></body></html>`;
    download(`language-proofing-${lang}-${stamp}.html`, html, 'text/html');
  }
  async function importFile(f: File) {
    if (!view) return;
    const raw = await f.text();
    const got: Record<string, string> = {};
    try {
      if (/^\s*[{[]/.test(raw)) {
        const j = JSON.parse(raw) as { lines?: { id: string; text: string }[] };
        for (const r of j.lines ?? []) if (r?.id && typeof r.text === 'string') got[r.id] = r.text;
      } else {
        const doc = new DOMParser().parseFromString(raw, 'text/html');
        doc.querySelectorAll('tr[data-id]').forEach((tr) => { const id = tr.getAttribute('data-id') || ''; const td = tr.querySelector('td.tx'); if (id && td) got[id] = (td.textContent || '').trim(); });
      }
    } catch { say('That file could not be read.'); return; }
    let n = 0;
    const next = { ...edits };
    for (const l of view.lines) {
      const v = got[l.id]; if (v === undefined) continue;
      const base = isEn ? l.english : l.text;
      if (v.trim() !== base) { next[l.id] = v.trim(); n++; }
    }
    setEdits(next); setOpenId(null);
    say(n ? `Imported - ${n} line${n > 1 ? 's' : ''} replaced, marked EDITED until Done` : 'Nothing in that file differs from what is saved.');
  }

  if (!open) return null;
  const zh = lang === 'zh';
  return (
    <>
      <div className="lp-scrim" onMouseDown={tryClose} />
      <aside className="lp-drawer" role="dialog" aria-label="Language proofing">
        <div className="lp-dh">
          <h3>Language proofing · {langTitle(lang)}</h3>
          <span className="lp-seg">
            {(view?.languages ?? [lang]).map((c) => (
              <button key={c} type="button" className={c === lang ? 'on' : ''} onClick={() => { if (c === lang) return; if (dirtyCount && !window.confirm('Your changes in this language are not saved. Switch anyway?')) return; setLang(c); }}>{c.toUpperCase()}</button>
            ))}
          </span>
          <button type="button" className="lp-x" title="Close (Esc)" onClick={tryClose}>✕</button>
        </div>
        <div className="lp-tools">
          <span className="lp-seg">
            <button type="button" className={filter === 'new' ? 'on' : ''} onClick={() => { setFilter('new'); closeLine(); }}>New</button>
            <button type="button" className={filter === 'chg' ? 'on' : ''} onClick={() => { setFilter('chg'); closeLine(); }}>Changed</button>
            <button type="button" className={filter === 'all' ? 'on' : ''} onClick={() => { setFilter('all'); closeLine(); }}>Everything</button>
          </span>
          <select value={stage} onChange={(e) => { setStage(e.target.value); closeLine(); }} aria-label="Stage">
            <option value="">All stages</option>
            {stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="lp-sp" />
          <details className="lp-menu"><summary className="lp-btn sm">Export ▾</summary>
            <div><button type="button" onClick={() => exportAs('html')}>Proofreading file (HTML)</button><button type="button" onClick={() => exportAs('json')}>JSON</button></div>
          </details>
          <button type="button" className="lp-btn sm" onClick={() => fileRef.current?.click()}>Import</button>
          <input ref={fileRef} type="file" accept=".json,.html,.htm" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); e.target.value = ''; }} />
        </div>
        <div className="lp-tools lp-tools2"><input value={q} onChange={(e) => { setQ(e.target.value); closeLine(); }} placeholder="Search a line…" aria-label="Search a line" /></div>
        <div className="lp-cnt">{loading ? 'Loading…' : `${shown.length} shown · ${counts.nw} new · ${counts.chg} changed · ${counts.all} lines`}</div>
        <div className="lp-body">
          <div className="lp-hdr"><div>{view ? langTitle(view.primary) : 'English'}</div><div>{isEn ? `${langTitle(lang)} (style)` : langTitle(lang)}</div></div>
          {shown.map((l) => {
            const tag = l.status === 'new' ? <span className="lp-tag t-new">NEW</span> : l.status === 'changed' ? <span className="lp-tag t-chg">CHANGED</span> : null;
            const edited = edits[l.id] !== undefined;
            const res = ai[l.id];
            const isOpen = openId === l.id;
            return (
              <div key={l.id} className={`lp-ln${isOpen ? ' edit' : ''}`}>
                <div>
                  <div className="lp-en"><button type="button" className="lp-play" aria-label="Play the English" onClick={() => play(l.english, view!.primary)}>▶</button><div>{l.english} {tag}</div></div>
                  <div className="lp-where"><b>{l.stage}</b> · {l.where}{l.machine ? ' · auto-translated' : ''}</div>
                </div>
                <div className="lp-col2" data-l={langTitle(lang)}>
                  {isOpen ? (
                    <>
                      <textarea ref={taRef} className={zh ? 'zh' : ''} rows={3} value={textOf(l)} readOnly={!canEdit}
                                onChange={(e) => setText(l, e.target.value)} aria-label={`${langTitle(lang)} line`} />
                      <div className="lp-ai">
                        <button type="button" className="lp-play" title="Hear what is in the box" onClick={() => play(textOf(l), lang)}>▶</button>
                        <button type="button" className="lp-btn sm" disabled={res?.busy} onClick={() => askAi(l, 'suggest')}>✨ AI Suggest</button>
                        <button type="button" className="lp-btn sm" disabled={res?.busy} onClick={() => askAi(l, 'check')}>✓ AI Check</button>
                        <span className="lp-mut">Esc closes the line</span>
                      </div>
                      {res && (
                        <div className="lp-res">
                          {res.busy ? <span className="lp-mut">Asking the AI…</span>
                            : !res.ok ? <span className="lp-mut">{AI_REASONS[res.reason ?? ''] ?? 'The AI could not answer - try again.'}</span>
                            : res.verdict === 'change' && res.suggestion ? (
                              <>
                                <span className="lp-mut lp-k">{res.mode === 'check' ? 'AI CHECK · one change suggested' : 'AI SUGGEST'}</span> ·{' '}
                                <span className={`lp-s${zh ? ' zh' : ''}`} title="Click to put it in the box" onClick={() => { if (canEdit) setText(l, res.suggestion!); }}>{res.suggestion}</span>
                                {res.why && <div className="lp-why">Why: {res.why}</div>}
                              </>)
                            : <><span className="lp-ok">✓ {res.mode === 'check' ? 'AI Check' : 'AI Suggest'}</span> · {res.why || 'reads naturally and means the same as the English.'}</>}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="lp-tr">
                        <button type="button" className="lp-play" aria-label="Play this line" onClick={() => play(textOf(l), lang)}>▶</button>
                        <span className={`lp-txt${zh ? ' zh' : ''}${textOf(l) ? '' : ' empty'}`} title="Click to edit" onClick={() => openLine(l.id)}>{textOf(l) || 'Not written yet - click to write it'}</span>
                      </div>
                      {edited && <div><span className="lp-tag t-ed">EDITED</span></div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && shown.length === 0 && <div className="lp-ln"><div className="lp-mut">Nothing in this filter.</div></div>}
        </div>
        <div className="lp-foot">
          <span className="lp-mut">{dirtyCount ? `${dirtyCount} line${dirtyCount > 1 ? 's' : ''} changed - Done saves` : reviewed.size ? `${reviewed.size} line${reviewed.size > 1 ? 's' : ''} read - Done marks them proofread` : 'No changes'}</span>
          <span className="lp-sp" />
          <button type="button" className="lp-btn" onClick={tryClose}>Cancel<span className="lp-kbd">Esc</span></button>
          <button type="button" className="lp-btn p" disabled={saving || !canEdit} title={canEdit ? undefined : 'Your group cannot save language lines (Settings › AI Agent)'} onClick={() => void done()}>{saving ? 'Saving…' : 'Done'}<span className="lp-kbd">Ctrl+Enter</span></button>
        </div>
        {toast && <div className="lp-toast">{toast}</div>}
      </aside>
    </>
  );
}
